import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import TokenManagement from './TokenManagement';
import PlanManagement from './PlanManagement';
import UsageStats from './UsageStats';
import ApiTesting from './ApiTesting';
import ErrorBoundary from './ErrorBoundary';

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('tokens');
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/'); // Redirect to home page after sign out
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Handle tab change - memoized to prevent unnecessary re-renders
  const handleTabChange = useCallback((tabName) => {
    setActiveTab(tabName);
  }, []);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">
            <a href="/" className="hover:text-blue-600">DescribeAPI</a> Dashboard
          </h1>
          
          <div className="flex items-center space-x-4">
            {user && (
              <div className="text-sm text-gray-700">
                {user.attributes?.name || user.attributes?.email}
              </div>
            )}
            <button
              onClick={handleSignOut}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => handleTabChange('tokens')}
            className={`py-2 px-4 text-center border-b-2 font-medium text-sm ${
              activeTab === 'tokens'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            API Tokens
          </button>
          <button
            onClick={() => handleTabChange('api-testing')}
            className={`py-2 px-4 text-center border-b-2 font-medium text-sm ${
              activeTab === 'api-testing'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            API Testing
          </button>
          <button
            onClick={() => handleTabChange('usage')}
            className={`py-2 px-4 text-center border-b-2 font-medium text-sm ${
              activeTab === 'usage'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Usage Statistics
          </button>
          <button
            onClick={() => handleTabChange('plan')}
            className={`py-2 px-4 text-center border-b-2 font-medium text-sm ${
              activeTab === 'plan'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Subscription Plan
          </button>
        </div>
        
        {/* Tab Content */}
        <div className="mt-6">
          <ErrorBoundary>
            {activeTab === 'tokens' && <TokenManagement />}
            {activeTab === 'api-testing' && <ApiTesting />}
            {activeTab === 'usage' && <UsageStats />}
            {activeTab === 'plan' && <PlanManagement />}
          </ErrorBoundary>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} DescribeAPI. All rights reserved.
            </div>
            <div className="flex space-x-6">
              <a href="mailto:support@describeapi.com" className="text-sm text-gray-500 hover:text-gray-900">
                Support
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
